import {
  Firestore,
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import { DbBusinessCalendar, DbCalendarStatus } from "./models";

export class CalendarService {
  private calendarsRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_entities",
    );
  }

  generateCalendarId(firestore: Firestore, businessId: string): string {
    return doc(this.calendarsRef(firestore, businessId)).id;
  }

  async getCalendars(
    businessId: string,
    enabledOnly: boolean = true,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCalendar[]> {
    let q = query(this.calendarsRef(firestore, businessId));
    if (enabledOnly) {
      q = query(q, where("calendar_status", "==", DbCalendarStatus.enable));
    } else {
      q = query(q, where("calendar_status", "!=", DbCalendarStatus.deleted));
    }
    const calendarsSnapshot = await getDocs(q);
    const calendars = calendarsSnapshot.docs.map((doc) => {
      const data = doc.data() as DbBusinessCalendar;
      return {
        ...data,
        index: data.index ?? 0,
      };
    });
    return calendars.sort((a, b) => a.index - b.index);
  }

  async getCalendar(
    businessId: string,
    calendarId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCalendar | null> {
    const calendar = await getDoc(
      doc(this.calendarsRef(firestore, businessId), calendarId),
    );
    if (!calendar.exists()) {
      return null;
    }
    return calendar.data() as DbBusinessCalendar;
  }

  async getCalendarsById(
    ids: { businessId: string; calendarId: string }[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCalendar[]> {
    const calendars = await Promise.all(
      ids.map((id) =>
        this.getCalendar(id.businessId, id.calendarId, firestore),
      ),
    );
    return calendars.filter(
      (calendar) => calendar !== null,
    ) as DbBusinessCalendar[];
  }

  async addCalendar(
    businessId: string,
    calendar: DbBusinessCalendar,
    firestore: Firestore = firebaseDb,
  ) {
    // get the highest index
    const calendars = await this.getCalendars(businessId, false, firestore);
    const highestIndex =
      calendars.length > 0
        ? Math.max(...calendars.map((c) => c.index ?? 0))
        : 0;

    calendar.id = this.generateCalendarId(firestore, businessId);
    calendar.index = highestIndex + 1;
    calendar.created_at = Timestamp.now();
    calendar.updated_at = Timestamp.now();
    await setDoc(
      doc(this.calendarsRef(firestore, businessId), calendar.id),
      calendar,
    );

    const newCalendar = await this.getCalendar(
      businessId,
      calendar.id,
      firestore,
    );
    return newCalendar;
  }

  async updateCalendar(
    businessId: string,
    calendar: DbBusinessCalendar,
    firestore: Firestore = firebaseDb,
  ) {
    calendar.updated_at = Timestamp.now();
    await setDoc(
      doc(this.calendarsRef(firestore, businessId), calendar.id),
      calendar,
    );

    const updatedCalendar = await this.getCalendar(
      businessId,
      calendar.id,
      firestore,
    );
    return updatedCalendar;
  }

  async updateCalendarStatus(
    businessId: string,
    calendarId: string,
    status: DbCalendarStatus,
    firestore: Firestore = firebaseDb,
  ) {
    await updateDoc(doc(this.calendarsRef(firestore, businessId), calendarId), {
      calendar_status: status,
      updated_at: Timestamp.now(),
    });
  }

  async deleteCalendar(
    businessId: string,
    calendarId: string,
    firestore: Firestore = firebaseDb,
  ) {
    await updateDoc(doc(this.calendarsRef(firestore, businessId), calendarId), {
      calendar_status: DbCalendarStatus.deleted,
      updated_at: Timestamp.now(),
    });
  }

  async updateCalendarOrder(
    businessId: string,
    calendars: DbBusinessCalendar[],
  ) {
    const calendarsRef = this.calendarsRef(firebaseDb, businessId);
    const batch = writeBatch(firebaseDb);

    calendars.forEach((calendar, index) => {
      const calendarRef = doc(calendarsRef, calendar.id);
      batch.update(calendarRef, { index });
    });

    await batch.commit();
    return calendars;
  }
}

export const calendarService = new CalendarService();
